import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Cross } from "@styled-icons/entypo/Cross";
import useWindowSize from "layout/useWindowSize";
import Modal from "components/Modal";
import Column from "components/Column";
import Row from "components/Row";
import Button from "components/Buttons";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";
import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";
import rcrmActions from "modules/rcrm/actions";
import rcrmSelectors from "modules/rcrm/selectors";
import Icon from "components/Icon";
import FacebookSettingContent from "components/facebook/FacebookSettingContent";
import GoogleSettingContent from "components/facebook/GoogleSettingContent";
import CRMSettingContent from "components/CRMSettingContent";
import Toast from "components/Toast";

const SettingModal = ({ show, setShow, status, currentTab }) => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const facebookLeadFormSettingResult = useSelector((state) => facebookSelectors.getLeadFormSettingResult(state));
  const facebookPageSettingResult = useSelector((state) => facebookSelectors.getFacebookPageSettingResult(state));
  const googleSettingResult = useSelector((state) => facebookSelectors.getGoogleSettingResult(state));
  const facebookUserInfoResult = useSelector((state) => facebookSelectors.getFacebookUserInfoResult(state));
  const changePageUrlInputResult = useSelector((state) => facebookSelectors.changePageUrlInputResult(state));
  const changePageFormSelectTypeResult = useSelector((state) =>
    facebookSelectors.changePageFormSelectTypeResult(state),
  );
  const setCheckedFormItemsResult = useSelector((state) => facebookSelectors.setCheckedFormItemsResult(state));
  const googleUserInfoResult = useSelector((state) => googleSelectors.getGoogleExchangeTokenResult(state));
  const changeSheetsUrlInputResult = useSelector((state) => facebookSelectors.changeSheetsUrlInputResult(state));
  const rcrmGateItem = useSelector((state) => rcrmSelectors.setGateItemResult(state));
  const googleSheetsError = useSelector((state) => googleSelectors.setValidateGoogleSheetsUrlResult(state));
  const facebookLoading = useSelector((state) => facebookSelectors.setFacebookLoadingResult(state));
  const facebookFormLoading = useSelector((state) => facebookSelectors.setFacebookFormLoadingResult(state));
  const googleLoading = useSelector((state) => googleSelectors.setGoogleLoadingResult(state));
  const googleSheetsLoading = useSelector((state) => googleSelectors.setGoogleSheetsLoadingResult(state));
  const rcrmLoading = useSelector((state) => rcrmSelectors.setCRMLoadingResult(state));
  const [statusLoading, setStatusLoading] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showFailedToast, setShowFailedToast] = useState(false);
  const windowSize = useWindowSize();

  /**
   * facebookLoading.data.loading = false if getFacebookPageSetting has finished loading
   * facebookFormLoading.data.loading = false if getFacebookFormList has finished loading
   * googleLoading.data.loading = false if the getGoogleSetting has finished loading
   * googleSheetsLoading.data.loading = false if the getGoogleSheetsList has finished loading
   */
  const checkGSheetsDataLoadingStatus =
    facebookLoading.data.loading ||
    facebookFormLoading.data.loading ||
    googleLoading.data.loading ||
    googleSheetsLoading.data.loading;

  /**
   * facebookLoading.data.loading = false if getFacebookPageSetting has finished loading
   * facebookFormLoading.data.loading = false if getFacebookFormList has finished loading
   * rcrmLoading.data.loading = false if the getCRMSetting has finished loading
   */
  const checkRCRMDataLoadingStatus =
    facebookLoading.data.loading || facebookFormLoading.data.loading || rcrmLoading.data.loading;

  /**
   * edit connection case
   * save button will disabled
   * if facebook data (left side) and google data (right side) are not finished loading
   * and some side has error
   */
  const disabledSaveButton =
    ((facebookPageSettingResult.result !== 0 ||
      (facebookPageSettingResult.result === 0 && facebookPageSettingResult.msg === "NO_DATA")) &&
      (googleSettingResult.result !== 0 ||
        (googleSettingResult.result === 0 && googleSettingResult.msg === "NO_DATA"))) ||
    (facebookLeadFormSettingResult.data.facebook_status === "error" &&
      (facebookUserInfoResult.result !== 0 ||
        (facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "NO_DATA")) &&
      facebookLeadFormSettingResult.data.google_status === "error" &&
      googleUserInfoResult.result !== 0) ||
    checkGSheetsDataLoadingStatus;

  /**
   * add connection case
   * save button will disabled
   * if facebook data (left side) and google data (right side) are not finished loading
   */
  const disabledSaveButtonAddConnection =
    (facebookUserInfoResult.result !== 0 ||
      (facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "NO_DATA") ||
      checkGSheetsDataLoadingStatus) &&
    (googleUserInfoResult.result !== 0 || checkGSheetsDataLoadingStatus);

  /**
   * reset state value after close modal
   */
  const resetLeadFormSetting = async () => {
    await dispatch(facebookActions.setValidateFacebookLeadForm({ result: false }));
    await dispatch(facebookActions.setFbConnected(false));
    await dispatch(facebookActions.setFbDisconnected(false));
    await dispatch(facebookActions.setGoogleConnected(false));
    await dispatch(facebookActions.setGoogleDisconnected(false));
    await dispatch(
      facebookActions.getFacebookUserInfo({
        token: rmpToken,
        userAccessToken: null,
      }),
    );
    await dispatch(
      facebookActions.getFacebookPageSetting({
        token: rmpToken,
        id: null,
      }),
    );
    await dispatch(
      googleActions.getGoogleExchangeToken({
        token: rmpToken,
        code: null,
      }),
    );
    await dispatch(
      facebookActions.getGoogleSetting({
        token: rmpToken,
        id: null,
      }),
    );
    await dispatch(googleActions.setGoogleSheetsError(false));
    await dispatch(facebookActions.setFacebookError(false));
    setStatusLoading(false);
  };

  /** close modal */
  const handleSheetsSettingCloseModal = async () => {
    setShow("hide");
    resetLeadFormSetting();
  };

  /**
   * reset state value after close modal
   */
  const resetCRMLeadFormSetting = async () => {
    await dispatch(facebookActions.setValidateFacebookLeadForm({ result: false }));
    await dispatch(facebookActions.setFbConnected(false));
    await dispatch(facebookActions.setFbDisconnected(false));
    await dispatch(
      facebookActions.getFacebookUserInfo({
        token: rmpToken,
        userAccessToken: null,
      }),
    );
    dispatch(
      facebookActions.getFacebookPageSetting({
        token: rmpToken,
        id: null,
      }),
    );
    await dispatch(facebookActions.setFacebookError(false));
    setStatusLoading(false);
  };

  /**CRM */
  const handleCRMSettingCloseModal = () => {
    setShow("hide");
    resetCRMLeadFormSetting();
  };

  ////// save lead form setting //////

  const getAllLeadFormSettings = async (tab) => {
    await dispatch(
      facebookActions.getAllLeadFormSettings({
        token: rmpToken,
        appReserve: tab,
      }),
    );
  };

  /** save facebook account to database (facebook_account table)*/
  const setFacebookAccount = async () => {
    const newAccessToken = await dispatch(
      facebookActions.getFacebookNewAccessToken({
        token: rmpToken,
        userAccessToken:
          facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED"
            ? facebookUserInfoResult.data.user_access_token
            : null,
      }),
    );

    if (newAccessToken.result === 0 && newAccessToken.data && newAccessToken.data.access_token) {
      await dispatch(
        facebookActions.setFacebookAccount({
          token: rmpToken,
          userAccessToken: newAccessToken.data.access_token,
        }),
      );
    }
  };

  /** save google account to database (google_account table) */
  const setGoogleAccount = async () => {
    await dispatch(
      googleActions.setGoogleAccount({
        token: rmpToken,
        tokens: googleUserInfoResult.result === 0 ? googleUserInfoResult.data.tokens : null,
      }),
    );
  };

  /**
   * save connection (Google Sheets)=> save to facebook_leadform_setting, facebook_lead_form, facebook_page_setting, google_sheets, selected_facebook_form_id table
   * @param {object} params
   */
  const setLeadFormSetting = async (params) => {
    const leadFormSetting = await dispatch(
      facebookActions.setLeadFormSetting({
        token: rmpToken,
        settingId:
          facebookLeadFormSettingResult.result === 0 && facebookLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
            ? facebookLeadFormSettingResult.data.id
            : 0,
        pageUrl: params.pageUrl,
        formSetting: params.formSetting,
        formId: params.formId,
        fbUserId: params.fbUserId,
        appReserve: "googleSheets",
      }),
    );

    const pageIdResult = await dispatch(
      facebookActions.getFacebookPageId({
        token: rmpToken,
        userAccessToken:
          facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED"
            ? facebookUserInfoResult.data.user_access_token
            : null,
        pageUrl: params.pageUrl,
      }),
    );

    const pageIdFromDB = await dispatch(
      facebookActions.getPageSetting({
        token: rmpToken,
        pageUrl: params.pageUrl,
      }),
    );

    const fbPageAccessToken = await dispatch(
      facebookActions.getFacebookPageAccessToken({
        token: rmpToken,
        userId: params.fbUserId,
        pageId:
          pageIdResult.result === 0 && pageIdResult.msg === "GET_DATA_COMPLETED"
            ? pageIdResult.data.id
            : pageIdFromDB.result === 0 && pageIdFromDB.data && pageIdFromDB.data.length > 0
            ? pageIdFromDB.data[0].fb_page_id
            : null,
      }),
    );

    /**
     * if duplicate form will show error in modal and can't save
     * if can't save facebook data (left side) will save only google data (right side)
     * if can't save google data (right side) will save only facebook data (left side)
     * if can't save both data will show failed toast
     */
    if (leadFormSetting.result !== 0 || (leadFormSetting.result === 0 && leadFormSetting.msg === "NO_DATA")) {
      /** save same form on same time */
      if (
        leadFormSetting.duplicateForm != undefined &&
        leadFormSetting.duplicateForm.length > 0 &&
        leadFormSetting.msg === "DUPLICATE_FORM_SELECTED"
      ) {
        await dispatch(
          facebookActions.getFacebookFormList({
            token: rmpToken,
            pageAccessToken:
              fbPageAccessToken.result === 0 && fbPageAccessToken.data && fbPageAccessToken.data.length > 0
                ? fbPageAccessToken.data[0].fb_token
                : null,
            pageUrl: params.pageUrl,
            appReserve: "googleSheets",
          }),
        );
        if (params.formSetting == "all") {
          await dispatch(facebookActions.setDuplicateFacebookForm({ duplicate: "all" }));
          await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "least_one_form" }));
        } else if (params.formSetting == "select") {
          const result = setCheckedFormItemsResult.data.checkedFormItems.filter(
            (item) => !leadFormSetting.duplicateForm.includes(item),
          );
          await dispatch(facebookActions.setCheckedFormItems(result));
          await dispatch(facebookActions.setDuplicateFacebookForm({ duplicate: leadFormSetting.duplicateForm }));
          await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "selected_form_have_used" }));
        }
        setStatusLoading(false);
      } else {
        const googleSheetsSetting = await dispatch(
          googleActions.setGoogleSheetsSetting({
            token: rmpToken,
            settingId:
              facebookLeadFormSettingResult.result === 0 && facebookLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
                ? facebookLeadFormSettingResult.data.id
                : leadFormSetting.result === 0 && leadFormSetting.msg !== "NO_DATA"
                ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                  ? leadFormSetting.data.lead_form_setting_id
                  : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                  ? leadFormSetting.data.data
                  : 0
                : 0,
            googleAccount: params.googleAccount,
            spreadsheetUrl: params.spreadsheetUrl,
            refreshToken: googleUserInfoResult.result === 0 ? googleUserInfoResult.data.tokens.refresh_token : null,
          }),
        );

        if (googleSheetsSetting.result === 0 && googleSheetsSetting.msg !== "NO_DATA") {
          setShowSuccessToast(true);
          setShow("hide");
          setStatusLoading(false);
          getAllLeadFormSettings("googleSheets");
          resetLeadFormSetting();
        } else {
          setShowFailedToast(true);
          setStatusLoading(false);
          setShow("hide");
          getAllLeadFormSettings("googleSheets");
          resetLeadFormSetting();
        }
      }
    } else {
      const googleSheetsSetting = await dispatch(
        googleActions.setGoogleSheetsSetting({
          token: rmpToken,
          settingId:
            facebookLeadFormSettingResult.result === 0 && facebookLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
              ? facebookLeadFormSettingResult.data.id
              : leadFormSetting.result === 0 && leadFormSetting.msg !== "NO_DATA"
              ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                ? leadFormSetting.data.lead_form_setting_id
                : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                ? leadFormSetting.data.data
                : 0
              : 0,
          googleAccount: params.googleAccount,
          spreadsheetUrl: params.spreadsheetUrl,
          refreshToken: googleUserInfoResult.result === 0 ? googleUserInfoResult.data.tokens.refresh_token : null,
        }),
      );

      if (leadFormSetting.result === 0 || (googleSheetsSetting.result === 0 && googleSheetsSetting.msg !== "NO_DATA")) {
        setShowSuccessToast(true);
        setShow("hide");
        setStatusLoading(false);
        getAllLeadFormSettings("googleSheets");
        resetLeadFormSetting();
      } else {
        setShowFailedToast(true);
        setStatusLoading(false);
        setShow("hide");
        getAllLeadFormSettings("googleSheets");
        resetLeadFormSetting();
      }
    }
  };

  /** action when click save button */
  const handleSaveSettingLeadForm = async () => {
    setStatusLoading(true);
    await setFacebookAccount();
    await setGoogleAccount();
    const params = {
      pageUrl:
        changePageUrlInputResult.data.pageUrl === ""
          ? facebookPageSettingResult.result === 0
            ? facebookPageSettingResult.msg === "GET_DATA_COMPLETED"
              ? facebookPageSettingResult.data.fb_page_url
              : null
            : null
          : changePageUrlInputResult.data.pageUrl,
      formSetting: changePageFormSelectTypeResult.data.pageFormSelectType,
      formId:
        changePageFormSelectTypeResult.data.pageFormSelectType == "all"
          ? "all"
          : setCheckedFormItemsResult.data.checkedFormItems,
      fbUserId:
        facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED"
          ? facebookUserInfoResult.data.id
          : facebookPageSettingResult.result === 0
          ? facebookPageSettingResult.msg === "GET_DATA_COMPLETED"
            ? facebookPageSettingResult.data.fb_account_id
            : null
          : null,
      googleAccount:
        googleUserInfoResult.result === 0
          ? googleUserInfoResult.data.googleAccount.google_account
          : googleSettingResult.result === 0
          ? googleSettingResult.msg == "GET_DATA_COMPLETED"
            ? googleSettingResult.data.google_account
            : null
          : null,
      spreadsheetUrl: changeSheetsUrlInputResult.data.sheetsUrl,
    };
    if (params.formSetting === "select" && params.formId.length === 0) {
      await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "least_one_form" }));
      setStatusLoading(false);
    } else if (params.formSetting === null && params.formId === null) {
      await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "form_not_found" }));
      setStatusLoading(false);
    } else if (
      params.formSetting === "" &&
      params.formId === "" &&
      ((facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED") ||
        (facebookPageSettingResult.result === 0 && facebookPageSettingResult.msg === "GET_DATA_COMPLETED"))
    ) {
      await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "least_one_form" }));
      setStatusLoading(false);
    } else if (params.spreadsheetUrl == "" && params.googleAccount != null) {
      await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: true, msg: "empty_url" }));
      setStatusLoading(false);
    } else if (Object.keys(googleSheetsError.data.validateSheets).length !== 0) {
      if (googleSheetsError.data.validateSheets.result === true) {
        await dispatch(
          googleActions.setValidateGGLeadsGoogleSheetsUrl({
            result: googleSheetsError.data.validateSheets.result,
            msg: googleSheetsError.data.validateSheets.msg,
          }),
        );
        setStatusLoading(false);
      } else {
        await setLeadFormSetting(params);
      }
    } else {
      await setLeadFormSetting(params);
    }
  };

  /**
   * save connection (R-CRM tab) -> save to facebook_leadform_setting, facebook_lead_form, facebook_page_setting, google_sheets, selected_facebook_form_id table
   * @param {object} params
   */
  const setCRMLeadFormSetting = async (params) => {
    const leadFormSetting = await dispatch(
      facebookActions.setLeadFormSetting({
        token: rmpToken,
        settingId:
          facebookLeadFormSettingResult.result === 0 && facebookLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
            ? facebookLeadFormSettingResult.data.id
            : 0,
        pageUrl: params.pageUrl,
        formSetting: params.formSetting,
        formId: params.formId,
        fbUserId: params.fbUserId,
        appReserve: "rcrm",
      }),
    );

    const pageIdResult = await dispatch(
      facebookActions.getFacebookPageId({
        token: rmpToken,
        userAccessToken:
          facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED"
            ? facebookUserInfoResult.data.user_access_token
            : null,
        pageUrl: params.pageUrl,
      }),
    );

    const pageIdFromDB = await dispatch(
      facebookActions.getPageSetting({
        token: rmpToken,
        pageUrl: params.pageUrl,
      }),
    );

    const fbPageAccessToken = await dispatch(
      facebookActions.getFacebookPageAccessToken({
        token: rmpToken,
        userId: params.fbUserId,
        pageId:
          pageIdResult.result === 0 && pageIdResult.msg === "GET_DATA_COMPLETED"
            ? pageIdResult.data.id
            : pageIdFromDB.result === 0 && pageIdFromDB.data && pageIdFromDB.data.length > 0
            ? pageIdFromDB.data[0].fb_page_id
            : null,
      }),
    );

    /**
     * if duplicate form will show error in modal and can't save
     * if can't save facebook data (left side) will save only R-CRM data (right side)
     * if can't save R-CRM data (right side) will save only facebook data (left side)
     * if can't save both data will show failed toast
     */
    if (leadFormSetting.result !== 0 || (leadFormSetting.result === 0 && leadFormSetting.msg === "NO_DATA")) {
      /**save same form on same time */
      if (
        leadFormSetting.duplicateForm != undefined &&
        leadFormSetting.duplicateForm.length > 0 &&
        leadFormSetting.msg === "DUPLICATE_FORM_SELECTED"
      ) {
        await dispatch(
          facebookActions.getFacebookFormList({
            token: rmpToken,
            pageAccessToken:
              fbPageAccessToken.result === 0 && fbPageAccessToken.data && fbPageAccessToken.data.length > 0
                ? fbPageAccessToken.data[0].fb_token
                : null,
            pageUrl: params.pageUrl,
            appReserve: "rcrm",
          }),
        );
        if (params.formSetting == "all") {
          await dispatch(facebookActions.setDuplicateFacebookForm({ duplicate: "all" }));
          await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "least_one_form" }));
        } else if (params.formSetting == "select") {
          const result = setCheckedFormItemsResult.data.checkedFormItems.filter(
            (item) => !leadFormSetting.duplicateForm.includes(item),
          );
          await dispatch(facebookActions.setCheckedFormItems(result));
          await dispatch(facebookActions.setDuplicateFacebookForm({ duplicate: leadFormSetting.duplicateForm }));
          await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "selected_form_have_used" }));
        }
        setStatusLoading(false);
      } else {
        const rcrmSetting = await dispatch(
          rcrmActions.saveCRMSettingLeadForm({
            token: rmpToken,
            settingId:
              facebookLeadFormSettingResult.result === 0 && facebookLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
                ? facebookLeadFormSettingResult.data.id
                : leadFormSetting.result === 0 && leadFormSetting.msg !== "NO_DATA"
                ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                  ? leadFormSetting.data.lead_form_setting_id
                  : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                  ? leadFormSetting.data.data
                  : 0
                : 0,
            targetType: "gate",
            gateId: rcrmGateItem.data.gateItem.gateId,
            gateName: rcrmGateItem.data.gateItem.gateName,
            userId: null,
            userEmail: null,
          }),
        );
        if (rcrmSetting.result === 0) {
          setShowSuccessToast(true);
          setShow("hide");
          setStatusLoading(false);
          getAllLeadFormSettings("rcrm");
          resetCRMLeadFormSetting();
        } else {
          setShowFailedToast(true);
          setStatusLoading(false);
          setShow("hide");
          getAllLeadFormSettings("rcrm");
          resetCRMLeadFormSetting();
        }
      }
    } else {
      const rcrmSetting = await dispatch(
        rcrmActions.saveCRMSettingLeadForm({
          token: rmpToken,
          settingId:
            facebookLeadFormSettingResult.result === 0 && facebookLeadFormSettingResult.msg === "GET_DATA_COMPLETED"
              ? facebookLeadFormSettingResult.data.id
              : leadFormSetting.result === 0 && leadFormSetting.msg !== "NO_DATA"
              ? leadFormSetting.msg == "UPDATE_DATA_COMPLETED"
                ? leadFormSetting.data.lead_form_setting_id
                : leadFormSetting.msg == "INSERT_DATA_COMPLETED"
                ? leadFormSetting.data.data
                : 0
              : 0,
          targetType: "gate",
          gateId: rcrmGateItem.data.gateItem.gateId,
          gateName: rcrmGateItem.data.gateItem.gateName,
          userId: null,
          userEmail: null,
        }),
      );
      if (leadFormSetting.result === 0 || rcrmSetting.result === 0) {
        setShowSuccessToast(true);
        setShow("hide");
        setStatusLoading(false);
        getAllLeadFormSettings("rcrm");
        resetCRMLeadFormSetting();
      } else {
        setShowFailedToast(true);
        setStatusLoading(false);
        setShow("hide");
        getAllLeadFormSettings("rcrm");
        resetCRMLeadFormSetting();
      }
    }
  };

  /** CRM */
  const handleSaveCRMSettingLeadForm = async () => {
    setStatusLoading(true);
    await setFacebookAccount();
    const params = {
      pageUrl:
        changePageUrlInputResult.data.pageUrl === ""
          ? facebookPageSettingResult.result === 0
            ? facebookPageSettingResult.msg === "GET_DATA_COMPLETED"
              ? facebookPageSettingResult.data.fb_page_url
              : null
            : null
          : changePageUrlInputResult.data.pageUrl,
      formSetting: changePageFormSelectTypeResult.data.pageFormSelectType,
      formId:
        changePageFormSelectTypeResult.data.pageFormSelectType == "all"
          ? "all"
          : setCheckedFormItemsResult.data.checkedFormItems,
      fbUserId:
        facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED"
          ? facebookUserInfoResult.data.id
          : facebookPageSettingResult.result === 0
          ? facebookPageSettingResult.msg === "GET_DATA_COMPLETED"
            ? facebookPageSettingResult.data.fb_account_id
            : null
          : null,
    };

    const target = await dispatch(
      rcrmActions.getLeadTarget({
        token: rmpToken,
      }),
    );

    if (params.formSetting === "select" && params.formId.length === 0) {
      await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "least_one_form" }));
      setStatusLoading(false);
    } else if (params.formSetting === null && params.formId === null) {
      await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "form_not_found" }));
      setStatusLoading(false);
    } else if (
      params.formSetting === "" &&
      params.formId === "" &&
      ((facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED") ||
        (facebookPageSettingResult.result === 0 && facebookPageSettingResult.msg === "GET_DATA_COMPLETED"))
    ) {
      await dispatch(facebookActions.setValidateFacebookLeadForm({ result: true, msg: "least_one_form" }));
      setStatusLoading(false);
    } else if (target.result == 0 && !target.gates.some((item) => item.gate_id === rcrmGateItem.data.gateItem.gateId)) {
      await dispatch(rcrmActions.setValidateGate({ result: true, msg: "gate_not_found" }));
      setStatusLoading(false);
    } else {
      await setCRMLeadFormSetting(params);
    }
  };

  const successToastCallback = (value) => {
    setShowSuccessToast(value);
  };

  const failedToastCallback = (value) => {
    setShowFailedToast(value);
  };

  ////// save lead form setting //////

  const modalSheetsSettingContent = (
    <ModalContentContainer>
      <StyledContainer>
        <StyledCrossIcon
          onClick={
            statusLoading
              ? null
              : currentTab === "googleSheets"
              ? handleSheetsSettingCloseModal
              : currentTab === "rcrm" && handleCRMSettingCloseModal
          }
          disabled={statusLoading}
        />
        <SettingContainer windowSize={windowSize.width} tab={currentTab}>
          <SettingSubContainer tab={currentTab}>
            <SettingTitle>{t("__fbleads_google_sheets_tab_setting_modal")}</SettingTitle>
            {status === "add" && (
              <>
                <Row columnSpacing="0" responsive className="setting-row">
                  <Column className="left-column">
                    <IconArea>
                      <CircleFBIcon>
                        <FBIcon src="/images/facebook.svg" />
                      </CircleFBIcon>
                    </IconArea>
                    <FacebookSettingContainer>
                      <FacebookSettingContent leadFormId={null} currentTab={currentTab} action="add" />
                    </FacebookSettingContainer>
                  </Column>
                  <Column className="icon-arrow">
                    {windowSize.width <= 834 ? (
                      <Icon name="icon-down-dir" size="48px" color="#aaa" />
                    ) : (
                      <Icon name="icon-right-dir" size="48px" color="#aaa" />
                    )}
                  </Column>
                  <Column className="right-column">
                    {currentTab === "googleSheets" ? (
                      <>
                        <IconArea>
                          <CircleIcon>
                            <SheetIcon src="/images/sheets.png" />
                          </CircleIcon>
                        </IconArea>
                        <SheetSettingContainer>
                          <GoogleSettingContent leadFormId={null} />
                        </SheetSettingContainer>
                      </>
                    ) : (
                      currentTab === "rcrm" && (
                        <>
                          <IconArea>
                            <CircleCRMIcon>
                              <CRMIcon src="/images/r-crm-icon.png" />
                            </CircleCRMIcon>
                          </IconArea>
                          <CRMSettingContainer>
                            <CRMSettingContent />
                          </CRMSettingContainer>
                        </>
                      )
                    )}
                  </Column>
                  {currentTab === "googleSheets" ? (
                    <ButtonContainer>
                      <Button
                        type="secondary"
                        onClick={handleSheetsSettingCloseModal}
                        disabled={statusLoading ? true : false}
                        className={statusLoading ? "disabled-button" : ""}
                      >
                        {t("__cancel")}
                      </Button>
                      <Button
                        type="loading"
                        onClick={() => handleSaveSettingLeadForm()}
                        loading={statusLoading}
                        disabled={disabledSaveButtonAddConnection ? true : false}
                        className={disabledSaveButtonAddConnection ? "disabled-button" : ""}
                      >
                        {t("__save")}
                      </Button>
                    </ButtonContainer>
                  ) : (
                    currentTab === "rcrm" && (
                      <ButtonContainer>
                        <Button
                          type="secondary"
                          onClick={handleCRMSettingCloseModal}
                          disabled={statusLoading ? true : false}
                          className={statusLoading ? "disabled-button" : ""}
                        >
                          {t("__cancel")}
                        </Button>
                        <Button
                          type="loading"
                          onClick={() => handleSaveCRMSettingLeadForm()}
                          loading={statusLoading}
                          disabled={checkRCRMDataLoadingStatus}
                          className={checkRCRMDataLoadingStatus ? "disabled-button" : ""}
                        >
                          {t("__save")}
                        </Button>
                      </ButtonContainer>
                    )
                  )}
                </Row>
              </>
            )}
            {status === "edit" &&
              (facebookLeadFormSettingResult.result === 0 &&
              facebookLeadFormSettingResult.msg === "GET_DATA_COMPLETED" ? (
                <>
                  <Row columnSpacing="0" responsive className="setting-row">
                    <Column className="left-column">
                      <IconArea>
                        <CircleFBIcon>
                          <FBIcon src="/images/facebook.svg" />
                        </CircleFBIcon>
                      </IconArea>
                      <FacebookSettingContainer>
                        <FacebookSettingContent
                          leadFormId={facebookLeadFormSettingResult.data.id}
                          currentTab={currentTab}
                          action="edit"
                        />
                      </FacebookSettingContainer>
                    </Column>
                    <Column className="icon-arrow">
                      {windowSize.width <= 834 ? (
                        <Icon name="icon-down-dir" size="48px" color="#aaa" />
                      ) : (
                        <Icon name="icon-right-dir" size="48px" color="#aaa" />
                      )}
                    </Column>
                    <Column className="right-column">
                      {currentTab === "googleSheets" ? (
                        <>
                          <IconArea>
                            <CircleIcon>
                              <SheetIcon src="/images/sheets.png" />
                            </CircleIcon>
                          </IconArea>
                          <SheetSettingContainer>
                            <GoogleSettingContent leadFormId={facebookLeadFormSettingResult.data.id} />
                          </SheetSettingContainer>
                        </>
                      ) : (
                        currentTab === "rcrm" && (
                          <>
                            <IconArea>
                              <CircleCRMIcon>
                                <CRMIcon src="/images/r-crm-icon.png" />
                              </CircleCRMIcon>
                            </IconArea>
                            <CRMSettingContainer>
                              <CRMSettingContent />
                            </CRMSettingContainer>
                          </>
                        )
                      )}
                    </Column>
                    {currentTab === "googleSheets" ? (
                      <ButtonContainer>
                        <Button
                          type="secondary"
                          onClick={handleSheetsSettingCloseModal}
                          disabled={statusLoading ? true : false}
                          className={statusLoading ? "disabled-button" : ""}
                        >
                          {t("__cancel")}
                        </Button>
                        <Button
                          type="loading"
                          onClick={() => {
                            handleSaveSettingLeadForm();
                          }}
                          loading={statusLoading}
                          disabled={disabledSaveButton ? true : false}
                          className={disabledSaveButton ? "disabled-button" : ""}
                        >
                          {t("__save")}
                        </Button>
                      </ButtonContainer>
                    ) : (
                      currentTab === "rcrm" && (
                        <ButtonContainer>
                          <Button
                            type="secondary"
                            onClick={handleCRMSettingCloseModal}
                            disabled={statusLoading ? true : false}
                            className={statusLoading ? "disabled-button" : ""}
                          >
                            {t("__cancel")}
                          </Button>
                          <Button
                            type="loading"
                            onClick={() => handleSaveCRMSettingLeadForm()}
                            loading={statusLoading}
                            disabled={checkRCRMDataLoadingStatus}
                            className={checkRCRMDataLoadingStatus ? "disabled-button" : ""}
                          >
                            {t("__save")}
                          </Button>
                        </ButtonContainer>
                      )
                    )}
                  </Row>
                </>
              ) : (
                <LoadingArea>
                  <StyledLoadingIcon></StyledLoadingIcon>
                </LoadingArea>
              ))}
          </SettingSubContainer>
        </SettingContainer>
      </StyledContainer>
    </ModalContentContainer>
  );

  return (
    <>
      <Modal
        state={show}
        content={modalSheetsSettingContent}
        maxWidth={windowSize.width <= 834 ? windowSize.width : "900px"}
        custom={true}
      />
      <Toast
        show={showSuccessToast}
        message={t("__save_success")}
        bgColor="#4CAF50"
        callback={successToastCallback}
      ></Toast>
      <Toast
        show={showFailedToast}
        message={t("__save_failed")}
        bgColor="#F44336"
        callback={failedToastCallback}
      ></Toast>
    </>
  );
};

export default SettingModal;

const ModalContentContainer = styled.div`
  margin-bottom: 20px;
  font-family: "Poppins", "Kanit", "Helvetica Neue", sans-serif;
`;

const StyledContainer = styled.div`
  font-size: 14px;
  color: #555;
  position: relative;

  .m-b-10 {
    margin-bottom: 10px;
  }

  @media screen and (max-width: 834px) {
    .links-container {
      display: flex;
      justify-content: center;
    }
  }
`;

const StyledCrossIcon = styled(Cross).attrs((props) => ({
  disabled: props.disabled,
}))`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 10px;
  width: 40px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};
`;

const SettingContainer = styled.div.attrs((props) => ({
  width: props.windowSize,
  height: props.tab === "googleSheets" ? "1055px" : "900px",
}))`
  display: flex;
  background-color: #fbfeff;
  border-radius: 10px;
  border: 1px solid #f5f9fc;
  padding: 20px;
  width: 900px;
  height: 600px;
  @media screen and (max-width: 834px) {
    width: ${(props) => props.width}px;
    height: ${(props) => props.height};
  }
`;

const SettingSubContainer = styled.div.attrs((props) => ({
  height: props.tab === "googleSheets" ? "360px" : "200px",
}))`
  line-height: 2.5em;
  width: 100%;

  .icon-row {
    display: flex;
    gap: 30px;
    height: 30px;
  }
  .left-icon-column {
    display: flex;
    justify-content: center;
    position: relative;
  }
  .right-icon-column {
    display: flex;
    justify-content: center;
    position: relative;
  }

  .setting-row {
    display: flex;
    gap: 0px;
  }
  .left-column {
    border: 1px solid #aaa;
    border-radius: 8px;
    height: 435px;
  }
  .icon-arrow {
    width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 435px;
    @media screen and (max-width: 834px) {
      height: 100px;
      width: 100%;
      position: relative;
      top: -15px;
    }
  }
  .right-column {
    border: 1px solid #aaa;
    border-radius: 8px;
    height: 435px;
    @media screen and (max-width: 834px) {
      height: ${(props) => props.height};
    }
  }
`;

const SettingTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: left;
  color: #575757;
  margin-bottom: 30px;
`;
const IconArea = styled.div`
  width: 100%;
  position: relative;
  top: -30px;
  display: flex;
  justify-content: center;
`;
const CircleFBIcon = styled.div`
  width: 90px;
  height: 60px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CircleIcon = styled.div`
  width: 70px;
  height: 60px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CircleCRMIcon = styled.div`
  width: 90px;
  height: 60px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SheetIcon = styled.img`
  width: 60px;
  height: 60px;
`;

const FBIcon = styled.img`
  width: 60px;
  height: 60px;
`;

const CRMIcon = styled.img`
  width: 60px;
  height: 60px;
`;

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: 180px;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  bottom: 0px;
  right: 20px;
  margin-bottom: 20px;

  > Button {
    min-width: 100px;
    margin-bottom: 0px;
  }

  > Button:first-child {
    margin-right: 10px;
  }

  .disabled-button {
    cursor: not-allowed;
  }
`;

/**Facebook column**/
const FacebookSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 20px 20px 20px;
  position: relative;
  top: -60px;
`;
/** Facebook Column */

/** Google Column */
const SheetSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 20px 20px 20px;
  position: relative;
  top: -60px;
`;
/** Google Column */

/** CRM Column */
const CRMSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 10px 20px 20px 20px;
  position: relative;
  top: -60px;
`;
/** CRM Column */
